// Svelte
import { getContext } from 'svelte';

// Types
import type { Logger, LoggerFactory } from '@amp/web-apps-logger/src';
import type {
    ContextualMenuItem,
    ContextualMenu as IContextualMenu,
} from '~/utils/contextual-menu/types';
import type I18N from '@amp/web-apps-localization/src';

// Actions
import { makeContextualMenuPerformAction } from '~/jet/models';

// ICONS
import { ICONS } from '~/constants/product-context-menu';
import { LruMap } from '@amp/web-apps-utils/src';
import { canShareLink } from '~/utils/share';
import type { ContextualMenuItemAction } from '@amp/web-app-components/src/components/ProductContextMenu/types';

const CONTEXT_NAME = 'contextualMenu';
// Menu Icons Map
const ICON_MAP = {
    play: ICONS.PlayIcon,
    favorite: ICONS.FavoriteIcon,
    unfavorite: ICONS.UnfavoriteIcon,
    plus: ICONS.AddToLibraryIcon,
    addToPlaylist: ICONS.AddToPlaylistIcon,
    link: ICONS.LinkIcon,
    share: ICONS.ShareIcon,
    trash: ICONS.DeleteFromLibraryIcon,
    edit: ICONS.PencilIcon,
};

export function getContextualMenu(): IContextualMenu {
    const contextualMenuInstance = getContext(CONTEXT_NAME) as ContextualMenu;
    if (!contextualMenuInstance) {
        throw new Error('getContextualMenu called before ContextualMenu.setup');
    }
    return contextualMenuInstance;
}

const SIZE_LIMIT = 10;

export class ContextualMenu {
    private itemsLruMap = new LruMap<
        string,
        Promise<ContextualMenuItem[]> | ContextualMenuItem[]
    >(SIZE_LIMIT);

    static setup(
        context: Map<string, unknown>,
        logger: LoggerFactory,
        $i18n: I18N,
    ) {
        const contextualMenuInstance = new ContextualMenu(logger, $i18n);
        context.set(CONTEXT_NAME, contextualMenuInstance);

        return contextualMenuInstance;
    }

    constructor(logger: LoggerFactory, $i18n: I18N) {
        this.log = logger.loggerFor('ContextualMenu');
        this.$i18n = $i18n;
    }

    private readonly log: Logger;
    private readonly $i18n: I18N;

    async getAllMediaItemsConfig(
        url: string,
    ): Promise<{ items: ContextualMenuItem[] }> {
        const items = await this.itemsLruMap.get(url);

        const sharingIndex =
            items?.findIndex(
                (item) => !!item.action?.type && item.action.type === 'share',
            ) ?? -1;

        if (sharingIndex > -1) {
            const shareUrl = items[sharingIndex].action.url;

            // Native share sheet does not include an option to copy the url,
            // even if it is enabled within the sheet itself in Edit Extensions...
            // Provide a direct Copy Link item to allow for this
            if (canShareLink(shareUrl)) {
                items.splice(sharingIndex + 1, 0, {
                    action: makeContextualMenuPerformAction(
                        'copy',
                        shareUrl,
                        undefined,
                    ) as unknown as ContextualMenuItemAction,
                    label: this.$i18n.t(
                        'AMP.Shared.ContextualSharing.CopyLink.Label',
                    ),
                    icon: 'link',
                });
            }
        }

        return {
            items:
                items?.map((item) =>
                    this.mapToContextItem(item.label, item.icon, item.action),
                ) || [],
        };
    }

    // clearing the item cache if an action is done
    clearItems(url: string) {
        this.itemsLruMap.set(url, null);
    }

    setItemsPromise(
        url: string,
        itemsPromise: Promise<ContextualMenuItem[]>,
    ): void {
        // this.log.info('Setting Items for URL:', url);
        this.itemsLruMap.set(url, itemsPromise);
    }

    mapToContextItem(
        label: string,
        iconKey: string,
        action?: () => void,
    ): ContextualMenuItem {
        return {
            label,
            icon: ICON_MAP[iconKey],
            action: action || (() => {}), // default to noop
        };
    }
}
